/* eslint-disable complexity */

/* eslint-disable max-lines-per-function */

/* eslint-disable promise/catch-or-return */

/* eslint-disable promise/always-return */
import React from 'react';

import debounce from 'lodash/debounce';

import { Button } from '@qality/common-components/src/atoms/Button';
import { Flex } from '@qality/common-components/src/atoms/Flex';
import { Header } from '@qality/common-components/src/atoms/Header';
import { Text } from '@qality/common-components/src/atoms/Text';
import { Issue } from '@qality/common-components/src/molecules/Issue';
import { SpinnerLoader } from '@qality/common-components/src/molecules/SpinnerLoader';
import { IssueTestType } from '@qality/common-model/src/TestType';
import { StorageOfUniqueServices } from '@qality/common-service';
import { DEFAULT_TEST_TYPE } from '@qality/common-utils/src/TestType.const';

import {
  SelectItemStyled,
  SelectOptionsListStyled,
  SelectViewContentStyled,
  SelectWrapperStyled,
} from './Select.styled';
import { SelectView } from './Select.view';
import { SelectOptionView } from './SelectOption.view';

export const Select = ({
  loadOptions,
  loadSearchOptions,
  onChange,
  value,
  issueTestTypes,
  testTypes,
}: any) => {
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSelectLoading, setIsSelectIsLoading] = React.useState(true);

  const [searchOptions, setSearchOptions] = React.useState([]);
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [filteredSearchOptions, setFilteredSearchOptions] = React.useState([]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen((i) => !i);
  };

  const handleClearSelection = () => {
    const removedOptions = [...selectedOptions];
    const removedOptionsIds = removedOptions.map((option) => option.issueId);

    // Bring back selected option always to the top of the search list
    setSearchOptions((options) => {
      const filteredOptions = options.filter(
        (option) => !removedOptionsIds.includes(option.issueId)
      );

      return [...removedOptions, ...filteredOptions];
    });

    setSelectedOptions([]);
  };

  const handleRemoveIssueById = (issueId) => {
    const selectedIssue = selectedOptions.find(
      (option) => option.issueId === issueId
    );

    // Bring back selected option always to the top of the search list
    setSearchOptions((options) => {
      // remove option from search results to change the order
      const filteredOptions = options.filter(
        (option) => option.issueId !== issueId
      );

      return [selectedIssue, ...filteredOptions];
    });

    setSelectedOptions((options) =>
      options.filter((issue) => issue.issueId !== issueId)
    );
  };

  const handleAddIssue = (issue) => {
    setSelectedOptions((options) => {
      options.push(issue);
      return [...options];
    });
  };

  const handleChangeSearchValue = (text) => {
    setSearchValue(text);
  };

  const debounceHandleChangeSearchValue = React.useCallback(
    debounce((text, alreadyLinkedIssueIds) => {
      setIsLoading(true);

      return loadSearchOptions(text, alreadyLinkedIssueIds)
        .then((res) => {
          setSearchOptions(res);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 350),
    []
  );

  React.useEffect(() => {
    // callback
    onChange(selectedOptions);
  }, [selectedOptions]);

  React.useEffect(() => {
    setFilteredSearchOptions(
      searchOptions?.filter(
        (issue) => !selectedOptions.find((i) => i.issueId === issue.issueId)
      )
    );
  }, [selectedOptions, searchOptions]);

  React.useEffect(() => {
    try {
      const alreadyLinkedIssueIds = selectedOptions.map(
        ({ issueId }) => issueId
      );
      debounceHandleChangeSearchValue(searchValue, alreadyLinkedIssueIds);
    } catch (e) {
      setIsLoading(false);
    }
  }, [searchValue]);

  /** Trigger search when all options was selected */
  React.useEffect(() => {
    if (filteredSearchOptions.length === 0 && searchOptions.length > 0) {
      try {
        const alreadyLinkedIssueIds = selectedOptions.map(
          ({ issueId }) => issueId
        );
        debounceHandleChangeSearchValue(searchValue, alreadyLinkedIssueIds);
      } catch (e) {
        setIsLoading(false);
      }
    }
  }, [filteredSearchOptions]);

  React.useEffect(() => {
    setIsSelectIsLoading(true);
    try {
      loadOptions(value)
        .then((res) => {
          setSelectedOptions(res);
        })
        .finally(() => {
          setIsSelectIsLoading(false);
        });
    } catch (e) {
      setIsSelectIsLoading(false);
    }
  }, []);

  const getTriggerDescription = () => {
    const description = [];
    const options = [...selectedOptions];

    for (let k = 0; k < selectedOptions.length; k++) {
      // 50 characters is based on 600px modal width, 50 characters will be displayed for sure
      if (description.join(', ').length > 50) {
        break;
      }

      const t = options.shift();
      description.push(t.issueKey);
    }

    if (!selectedOptions.length) {
      return 'Search issues...';
    }

    return `${description.join(', ')}${
      options.length ? ` (+${options.length})` : ''
    }`;
  };

  const getIssueTestType = (issueId: number): IssueTestType | undefined => {
    return issueTestTypes.find(
      (details) => details.testCaseId === Number(issueId)
    );
  };

  const getTestType = (issue) => {
    if (!StorageOfUniqueServices.get('LicenseService').isAdvanced) {
      return DEFAULT_TEST_TYPE;
    }

    const issueTestType = getIssueTestType(issue.issueId);

    return issueTestType?.testTypeId
      ? testTypes.find((type) => type.id === issueTestType.testTypeId)
      : null;
  };

  const getTestTypeProps = (issue) => {
    const testType = getTestType(issue);
    return {
      testType: testType,
      isLoading: !testType,
      isReadOnly: true,
      style: { paddingTop: '2px', paddingLeft: '4px', paddingRight: '4px' },
    };
  };

  return (
    <SelectView
      handleClose={handleClose}
      handleToggle={handleToggle}
      isOpen={isOpen}
      searchValue={searchValue}
      onChangeText={handleChangeSearchValue}
      triggerDescription={getTriggerDescription()}
    >
      <>
        {selectedOptions.length > 0 && (
          <Button
            fontSize='small'
            spacing='none'
            onClick={handleClearSelection}
            appearance='subtle-link'
            size='small'
          >
            Clear selected items
          </Button>
        )}

        <SelectWrapperStyled>
          <SelectViewContentStyled>
            {selectedOptions.length > 0 && (
              <>
                <SelectItemStyled>
                  <Header spacing='small' size='xsmall'>
                    Selected
                  </Header>
                </SelectItemStyled>
                {isSelectLoading ? (
                  <Flex justifyCenter paddingSize='small'>
                    <SpinnerLoader />
                  </Flex>
                ) : (
                  <SelectOptionsListStyled>
                    {selectedOptions.map((issue, idx) => (
                      <SelectOptionView
                        key={issue.issueId + `-${idx}`}
                        defaultChecked
                        onChange={handleRemoveIssueById.bind(
                          this,
                          issue.issueId
                        )}
                      >
                        <Issue
                          issueKey={issue.issueKey}
                          title={issue.title}
                          testTypeProps={getTestTypeProps(issue)}
                        />
                      </SelectOptionView>
                    ))}
                  </SelectOptionsListStyled>
                )}
              </>
            )}

            <SelectItemStyled>
              <Header spacing='small' size='xsmall'>
                All
              </Header>
            </SelectItemStyled>
            {isLoading && (
              <Flex justifyCenter paddingSize='small'>
                <SpinnerLoader />
              </Flex>
            )}
            {!isLoading && !searchOptions.length && (
              <SelectItemStyled>
                <Text inactive>
                  We couldn’t find issues matching your search
                </Text>
              </SelectItemStyled>
            )}
            {!isLoading && filteredSearchOptions.length > 0 && (
              <SelectOptionsListStyled>
                {filteredSearchOptions.map((issue, idx) => (
                  <SelectOptionView
                    key={issue.issueId + `-${idx}`}
                    isChecked={false}
                    onChange={handleAddIssue.bind(this, issue)}
                  >
                    <Issue
                      issueKey={issue.issueKey}
                      title={issue.title}
                      testTypeProps={getTestTypeProps(issue)}
                    />
                  </SelectOptionView>
                ))}
              </SelectOptionsListStyled>
            )}
          </SelectViewContentStyled>
        </SelectWrapperStyled>
      </>
    </SelectView>
  );
};
